/**
 * @file 登录页面
 * @description 提供用户登录功能，包含动态背景、表单验证、多平台选择等
 * @author 智慧云行
 * @version 1.2.0
 */
<template>
  <div id="login" :class="{ 'page-loaded': !pageLoading, 'login-success': loginSuccess }">
    <!-- 动态粒子背景 -->
    <div class="particles-bg">
      <div class="particle" v-for="n in config.animation.particleCount" :key="n" :style="getParticleStyle(n)"></div>
    </div>
    
    <!-- 浮动装饰元素 -->
    <div class="floating-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
      <div class="shape shape-4"></div>
    </div>
    
    <!-- 页面加载动画 -->
    <transition name="fade">
      <div v-if="pageLoading" class="page-loader">
        <div class="loader-content">
          <div class="loader-logo">
            <div class="loader-spinner"></div>
          </div>
          <p class="loader-text">{{ config.texts.loading }}<span class="loading-dots"><span>.</span><span>.</span><span>.</span></span></p>
        </div>
      </div>
    </transition>
    <div class="records" v-if="footerConfig.copyright">
      Copyright © {{ footerConfig.copyright.year }} {{ footerConfig.copyright.allRightsReserved }} 
      <a :href="footerConfig.copyright.companyUrl" target="_blank">{{ footerConfig.copyright.company }}</a> 版权所有
      <template v-if="footerConfig.icp"> | <a :href="footerConfig.icp.url" target="_blank">{{ footerConfig.icp.number }}</a></template>
      <template v-if="footerConfig.links && footerConfig.links.length">
        <span v-for="link in footerConfig.links" :key="link.id">
          | <a href="javascript:void(0)" @click="viewAgreement(link.type)">{{ link.name }}</a>
        </span>
      </template>
    </div>
    <div class="box">
      <div class="box-text">
        <p id="mainTitle">{{ info.mainTitle || null }}</p>
        <p class="remark">{{ info.viceTitle || null }}</p>
      </div>
      <img class="img" src="../../assets/img/login.png" alt />
      <div class="form">
        <div class="title">
          <img v-if="info.logo" style="border-radius: 12px" :src="mixinQiniuImg(info.logo)" />
          <img v-else src="../../assets/img/logo.png" />
          <div id="companyName" style="width: 390px;">{{ info.name || null }}</div>
        </div>
        <div class="input-wrapper">
          <div class="input-with-icon">
            <i class="el-icon-user input-icon"></i>
            <login-input 
              :maxlength="config.validation.phoneMaxLength" 
              forId="phone" 
              :placeholder="config.texts.phonePlaceholder" 
              :value.sync="form.phone"
              @blur="validatePhone">
            </login-input>
          </div>
          <transition name="fade">
            <div v-if="formErrors.phone" class="error-tip">{{ formErrors.phone }}</div>
          </transition>
        </div>
        <div class="input-wrapper">
          <div class="input-with-icon">
            <i class="el-icon-lock input-icon"></i>
            <div class="password-input-wrapper">
              <login-input 
                :maxlength="config.validation.passwordMaxLength" 
                forId="password" 
                :placeholder="config.texts.passwordPlaceholder" 
                :value.sync="form.password"
                :type="showPassword ? 'text' : 'password'"
                @blur="validatePassword">
              </login-input>
              <i 
                class="password-toggle" 
                :class="showPassword ? 'el-icon-view' : 'el-icon-view-hide'"
                @click="showPassword = !showPassword"
                :title="config.texts.passwordToggleTitle">
              </i>
            </div>
          </div>
          <transition name="fade">
            <div v-if="formErrors.password" class="error-tip">{{ formErrors.password }}</div>
          </transition>
        </div>
        <div class="checkbox-group">
          <el-checkbox v-model="isSave" @change="isSaveChangeFn">{{ config.texts.rememberPassword }}</el-checkbox>
        </div>
        <div class="agreement-checkbox">
          <el-checkbox v-model="agreeAgreement">
            <span class="agreement-text">
              {{ config.texts.agreementPrefix }}
              <a href="javascript:void(0)" @click.stop="viewAgreement('agreement')">{{ config.texts.userAgreement }}</a>
              {{ config.texts.and }}
              <a href="javascript:void(0)" @click.stop="viewAgreement('privacy')">{{ config.texts.privacyPolicy }}</a>
            </span>
          </el-checkbox>
        </div>
        <el-button :loading="loading" :disabled="disable || !agreeAgreement" class="login-btn" 
          :class="[(disable || !agreeAgreement) ? 'disable' : '', { 'btn-shake': btnShake }]"
          @click="loginFn">
          <i v-if="!loading" class="el-icon-right btn-icon"></i>
          {{ config.texts.loginButton }}
        </el-button>
      </div>
    </div>
    <el-dialog top="0" class="dialog-apply-modal" :visible.sync="manyCompanyBox.show" :title="config.texts.selectPlatformTitle" width="600px"
      :footer-hide="true">
      <p class="tip">{{ config.texts.selectPlatformTip }}</p>
      <div class="many-company-row-list">
        <div class="many-company-row" v-for="(company, index) in manyCompanyBox.content" :key="index"
          @click="clickManyCompanyFn(company)">
          <div class="logo">
            <img :src="mixinQiniuImg(company.companyLogo)" />
          </div>
          <div class="left">
            <div class="name">{{ company.companyName }}</div>
            <div class="error" v-if="company.status == 0">
              {{ config.texts.accountDisabled }}
            </div>
            <div class="success" v-else-if="company.isPassword">
              {{ config.texts.passwordCorrect }}
            </div>
            <div class="error" v-else>{{ config.texts.passwordIncorrect }}</div>
          </div>
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>
    </el-dialog>

    <!-- 欠费提示弹窗 -->
    <el-dialog :title="billTitle" width="520px" :visible.sync="billVisible" :before-close="() => (billVisible = false)"
      :append-to-body="true" class="arrears-dialog" :close-on-click-modal="false">
      <div v-if="billType == 1" class="arrears-content">
        <!-- 警告图标 -->
        <div class="arrears-icon">
          <i class="el-icon-warning"></i>
        </div>
        
        <!-- 欠费金额 -->
        <div class="arrears-amount">
          <span class="label">{{ config.texts.arrearsPrefix }}</span>
          <span class="amount">￥{{ amount }}</span>
        </div>
        
        <!-- 提示文字 -->
        <div class="arrears-tip">
          {{ config.texts.arrearsSuffix }}
        </div>
        
        <!-- 账单明细 -->
        <div class="bill-section">
          <div class="bill-title">
            <i class="el-icon-document"></i>
            {{ config.texts.billDetail }}
          </div>
          
          <el-table :data="tableDataBill" border size="small" max-height="280" class="bill-table">
            <el-table-column v-for="(item, index) in tableColumnBill" :key="index" :prop="item.prop" :label="item.label"
              :width="item.width || null" align="center">
              <template slot-scope="{ row }">
                <div v-if="item.prop === 'time'" class="cell-time">
                  {{ $utils.timeFn(row.startTime, "YYYY/MM/DD") }} -
                  {{ $utils.timeFn(row.endTime, "YYYY/MM/DD") }}
                </div>

                <div v-else-if="item.prop === 'ratio'" class="cell-ratio">
                  {{ row.ratio }}%
                </div>

                <div v-else-if="item.prop === 'totalRealFee' || item.prop === 'serviceFee'" class="cell-money">
                  {{ row[item.prop] ? "￥" + row[item.prop] : "-" }}
                </div>

                <span v-else>{{ row[item.prop] ? row[item.prop] : "-" }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        
        <!-- 操作按钮 -->
        <div class="arrears-actions">
          <el-button @click="billVisible = false" class="btn-cancel">稍后处理</el-button>
          <el-button @click="renew" type="primary" class="btn-renew">
            <i class="el-icon-wallet"></i>
            {{ config.texts.renewButton }}
          </el-button>
        </div>
      </div>
      
      <!-- 系统暂停使用提示 -->
      <div v-else class="suspend-content">
        <div class="suspend-icon">
          <i class="el-icon-warning-outline"></i>
        </div>
        <div class="suspend-message">{{ billContent }}</div>
        <el-button @click="billVisible = false" type="primary" class="btn-confirm">我知道了</el-button>
      </div>
    </el-dialog>

    <!-- 续费二维码弹窗 -->
    <el-dialog :title="config.texts.renewTitle" width="400px" :visible.sync="codeVisible" :before-close="beforeClose" 
      :append-to-body="true" class="qrcode-dialog" :close-on-click-modal="false">
      <div class="qrcode-content">
        <div class="qrcode-wrapper" id="qrcode"></div>
        <div class="qrcode-hint">
          <i class="el-icon-mobile-phone"></i>
          {{ config.texts.qrcodeHint }}
        </div>
        <div class="qrcode-amount">
          充值金额：<span>￥{{ amount }}</span>
        </div>
        <div class="qrcode-timer">
          <i class="el-icon-time"></i>
          二维码有效期 2 分钟
        </div>
      </div>
    </el-dialog>

    <!-- 协议弹窗 -->
    <el-dialog 
      :title="agreementDialog.title" 
      width="760px" 
      :visible.sync="agreementDialog.visible" 
      :append-to-body="true" 
      class="agreement-dialog"
      :close-on-click-modal="false">
      <div class="agreement-dialog-content" v-loading="agreementDialog.loading">
        <div class="agreement-meta">
          <span class="version">版本：{{ agreementDialog.data.version }}</span>
          <span class="divider">|</span>
          <span class="update-time">更新时间：{{ formatAgreementTime(agreementDialog.data.updateTime) }}</span>
        </div>
        <div class="agreement-body">
          <markdown-viewer
            v-if="agreementDialog.data.isMarkdown"
            :content="agreementDialog.data.content">
          </markdown-viewer>
          <div v-else v-html="agreementDialog.data.content"></div>
        </div>
      </div>
      <div slot="footer" class="agreement-dialog-footer">
        <el-button type="primary" @click="handleAgreementConfirm">我已阅读</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import QRCode from "../../assets/js/qrcode.min.js";
import loginInput from "../../components/loginInput";
import loginMock from '@/assets/js/loginMock'
import externalLinks from '@/assets/json/externalLinks'
import { getDeviceFingerprint, encryptLoginRequest, aesDecrypt as aesDecryptFn } from '@/assets/js/security.js'
import MarkdownViewer from '@/components/markdownViewer.vue'
import { confirmProtocol, USER_TYPE } from '@/assets/js/protocolConfirm.js'

// 登录页面配置
const loginConfig = externalLinks.login

export default {
  components: { loginInput, MarkdownViewer },
  name: "login",
  data() {
    return {
      // 配置引用
      config: loginConfig,
      info: {},
      isSave: false,
      agreeAgreement: false, // 是否同意协议
      loading: false, // 登录按钮loading
      pageLoading: true, // 页面加载状态
      loginSuccess: false, // 登录成功状态
      btnShake: false, // 按钮抖动效果
      form: {}, // 登录表单对象
      formErrors: { // 表单错误提示
        phone: '',
        password: ''
      },
      showPassword: false, // 是否显示密码
      manyCompanyBox: {
        content: [],
        show: false,
      },
      disable: true, // 是否禁用
      footerConfig: {}, // footer配置
      billVisible: false, // 账单
      billTitle: loginConfig.texts.systemArrears,
      billType: 2,
      billContent: "",
      qrCode: null,
      timer: null,
      codeVisible: false,
      amount: 0,
      tableColumnBill: loginConfig.billColumns,
      tableDataBill: [],
      // 协议弹窗
      agreementDialog: {
        visible: false,
        loading: false,
        title: '',
        data: {
          title: '',
          version: '',
          updateTime: null,
          content: ''
        }
      },
    };
  },
  watch: {
    // 监听表单
    form: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val.phone && val.password) {
          this.disable = false;
        } else {
          this.disable = true;
        }
      },
    },
  },
  methods: {
    /**
     * 生成粒子样式
     */
    getParticleStyle(index) {
      const random = (min, max) => Math.random() * (max - min) + min
      return {
        left: `${random(0, 100)}%`,
        top: `${random(0, 100)}%`,
        width: `${random(2, 6)}px`,
        height: `${random(2, 6)}px`,
        animationDelay: `${random(0, 5)}s`,
        animationDuration: `${random(10, 20)}s`,
        opacity: random(0.3, 0.8)
      }
    },
    
    adjustFontSize(container, maxWidth) {
      const text = container.innerHTML;
      const style = window.getComputedStyle(container);
      const originalFontSize = parseFloat(style.fontSize);
      let fontSize = originalFontSize;

      container.innerHTML = '';
      const testDiv = document.createElement('div');
      testDiv.style.position = 'absolute';
      testDiv.style.visibility = 'hidden';
      testDiv.style.fontSize = `${fontSize}px`;
      testDiv.style.whiteSpace = 'nowrap';
      testDiv.style.wordWrap = 'normal';
      testDiv.innerHTML = text;

      document.body.appendChild(testDiv);
      let width = testDiv.offsetWidth;

      while (width > maxWidth && fontSize > 1) {
        fontSize -= 2;
        testDiv.style.fontSize = `${fontSize}px`;
        width = testDiv.offsetWidth;
      }

      fontSize = fontSize > 32 ? 32 : fontSize;
      fontSize = fontSize < 12 ? 12 : fontSize;
      container.style.fontSize = `${fontSize}px`;
      container.innerHTML = text;
      document.body.removeChild(testDiv);
    },
    /**
     * 获取系统logo和标题
     */
    async getSystemInfo() {
      let url = "/api/v1/common/title";
      let data = await this.$axios.get(url);
      if (data && data.code === 1 && data.data) {
        this.info = data.data;
        this.$nextTick(() => {
          const container =  document.getElementById('companyName')
          const maxWidth = container.offsetWidth;
          this.adjustFontSize(container, maxWidth);
          // const mainTitle =  document.getElementById('mainTitle')
          // console.log('container::: ', mainTitle);
          // const maxWidth1 = mainTitle.offsetWidth;
          // this.adjustFontSize(mainTitle, maxWidth1);
        })
      }
    },
    asyncToGenerator(generatorFunc) {
      return function () {
        const gen = generatorFunc.apply(this, arguments);
        return new Promise((resolve, reject) => {
          function step(key, arg) {
            let generatorResult;

            try {
              generatorResult = gen[key](arg);
            } catch (error) {
              return reject(error);
            }

            const { value, done } = generatorResult;

            if (done) {
              return resolve(value);
            } else {
              return Promise.resolve(value).then(
                function onResolve(val) {
                  step("next", val);
                },
                function onReject(err) {
                  step("throw", err);
                }
              );
            }
          }
          step("next");
        });
      };
    },
    /**
     * 记住账号密码
     */
    isSaveChangeFn(val) {
      if (!val) {
        sessionStorage.removeItem("login");
      }
      sessionStorage.setItem("isSave", val);
    },
    /**
     * 验证手机号
     */
    validatePhone() {
      const { validation } = this.config
      if (!this.form.phone) {
        this.formErrors.phone = validation.phoneEmptyError
        return false
      }
      if (this.form.phone.length === validation.phoneMaxLength && !/^1\d{10}$/.test(this.form.phone)) {
        this.formErrors.phone = validation.phoneFormatError
        return false
      }
      this.formErrors.phone = ''
      return true
    },
    /**
     * 验证密码
     */
    validatePassword() {
      const { validation } = this.config
      if (!this.form.password) {
        this.formErrors.password = validation.passwordEmptyError
        return false
      }
      if (this.form.password.length < validation.passwordMinLength) {
        this.formErrors.password = validation.passwordLengthError
        return false
      }
      this.formErrors.password = ''
      return true
    },
    /**
     * 登录
     * @returns {Promise<void>}
     */
    async loginFn(company) {
     
      const phoneValid = this.validatePhone()
      const passwordValid = this.validatePassword()

      if (!phoneValid || !passwordValid) {
        // 触发按钮抖动效果
        this.btnShake = true
        setTimeout(() => { this.btnShake = false }, this.config.animation.shakeDelay)
        return
      }

      if (!this.agreeAgreement) {
        this.$message.warning(this.config.texts.agreementWarning)
        // 触发按钮抖动效果
        this.btnShake = true
        setTimeout(() => { this.btnShake = false }, this.config.animation.shakeDelay)
        return
      }
      this.loading = true;
      let params = this.$utils.deepCopyFn(this.form);
      params.password = this.$utils.sha256Fn(params.password);
      if (company && company.formKey) params.formKey = company.formKey;
      if (company && company.organizationId)
        params.organizationId = company.organizationId;
      // 设备指纹（服务端会话绑定）
      params.deviceFingerprint = await getDeviceFingerprint();

      console.log('👉 进入加密登录逻辑，断点行即将执行') // 加在try块最开头
      let data
        try {
        data = await this._doSecureLogin(params)
      } catch (e) {
        console.error('加密登录失败，尝试降级到普通登录:', e)
        this.loading = false
        // 尝试降级到普通登录
        try {
          data = await this.$axios.post('/api/v1/manage/login', params)
          console.log('降级登录响应:', data)
        } catch (fallbackError) {
          console.error('降级登录也失败:', fallbackError)
          this.btnShake = true
          setTimeout(() => { this.btnShake = false }, this.config.animation.shakeDelay)
          this.$message.error('登录服务不可用，请检查网络连接或联系管理员')
          // 预热新的公钥，方便用户再次尝试
          this._preloadPubkey()
          return
        }
      }
      this.loading = false;

      if (data && data.code === 1) {
        // 登录成功动画
        this.loginSuccess = true
        this.$message.success(this.config.texts.loginSuccess)

        this.$store.dispatch("acLogin", data.data);
        // 记住账号密码：仅保存手机号，不保存任何形式的密码（安全考量）
        if (this.isSave) {
          sessionStorage.setItem("login", JSON.stringify({ phone: this.form.phone }));
        }

        // 延迟跳转，展示成功动画
        setTimeout(() => {
          let permissions = this.$store.getters.permissionsGetter.split(",").map((value) => Number(value));
          if (permissions.includes(this.config.permissions.workbenchId)) {
            this.$router.replace(this.config.routes.workbench);
          } else {
            this.$router.replace(this.config.routes.frame);
          }
        }, this.config.animation.redirectDelay)
      } else if (data.code === 50030) {
        this.getLoginCompany(params);
      } else if (data.code === 50200) {
        this.$message.error(data.msg);
        let arrears = await this.$axios.get(
          "/api/v1/manage/systems/costCenter/arrears",
          params
        );
        if (arrears.code == 50200) {
          this.billType = 2;
          this.billTitle = this.config.texts.systemSuspended;
          this.billContent = arrears.msg;
        } else {
          this.billType = 1;
          this.billTitle = this.config.texts.systemArrears;
          this.amount = arrears.data.amount;
          arrears.data.billVos.sort((a, b) => {
            return b.startTime - a.startTime;
          });
          this.tableDataBill = arrears.data.billVos;
        }
        this.billVisible = true;
      }
    },
    async renew() {
      let data = await this.$axios.post("/api/v1/pay/recharge", {
        amount: this.amount,
      });

      if (data.code == 1) {
        this.codeVisible = true;
        this.$nextTick(() => {
          if (this.qrCode) {
            this.qrCode.clear();
            this.qrCode.makeCode(data.data.qrCode);
          } else {
            this.qrCode = new QRCode(document.getElementById("qrcode"), {
              text: data.data.qrCode,
              width: this.config.qrcode.size,
              height: this.config.qrcode.size,
            });
          }
        });

        let numS = 0;
        this.timer = setInterval(async () => {
          let res = await this.$axios.get("/api/v1/pay/recharge/status", {
            outTradeNo: data.data.outTradeNo,
          });
          if (res?.data?.status == 2) {
            clearInterval(this.timer);
            this.qrCode.clear();
            this.codeVisible = false;
            this.billVisible = false;
            this.$message.success(this.config.texts.rechargeSuccess);
          }
          numS += this.config.qrcode.pollIncrement;
          if (numS >= this.config.qrcode.timeout) {
            clearInterval(this.timer);
            this.codeVisible = false;
            this.$message.info(this.config.texts.qrcodeExpired);
          }
        }, this.config.qrcode.pollInterval);
      }
    },
    beforeClose() {
      this.codeVisible = false;
      clearInterval(this.timer);
    },
    /**
     * enter事件
     * @param ev
     */
    keydownFn(ev) {
      if (
        this.$route.path === "/login" &&
        ev.keyCode === 13 &&
        !this.dialogApplyIsShow &&
        !this.loading
      ) {
        // 登录页，enter键，试用弹框隐藏，登录按钮--未请求中
        this.loginFn();
      }
    },
    /**
     * 预加载RSA公钥并缓存（页面挂载后立即调用）
     * 避免登录时实时请求pubkey导致的延迟和keyId在等待中过期
     */
    async _preloadPubkey() {
      try {
        const res = await this.$axios.get('/api/v1/manage/login/pubkey', { _t: Date.now() })
        if (res && res.code === 1 && res.data) {
          this._pubkeyCache = { ...res.data, cachedAt: Date.now() }
        }
      } catch (e) {
        // 预加载失败不影响登录流程，登录时会重新获取
      }
    },
    /**
     * 获取有效的RSA公钥（优先用缓存，4分钟内有效）
     * @returns {Promise<{keyId, publicKey}|null>}
     */
    async _getPubkey() {
      const MAX_CACHE_AGE = 4 * 60 * 1000 // 4分钟（后端5分钟过期，留1分钟余量）
      const now = Date.now()
      if (this._pubkeyCache && (now - this._pubkeyCache.cachedAt) < MAX_CACHE_AGE) {
        return this._pubkeyCache
      }
      // 缓存过期或不存在，重新获取
      try {
        console.log('正在获取公钥，请求URL:', '/api/v1/manage/login/pubkey')
        console.log('当前baseURL:', this.$axios.defaults.baseURL)
        const res = await this.$axios.get('/api/v1/manage/login/pubkey', { _t: now })
        console.log('公钥获取响应:', res)
        if (res && res.code === 1 && res.data) {
          this._pubkeyCache = { ...res.data, cachedAt: now }
          return this._pubkeyCache
        }
      } catch (error) {
        console.error('获取公钥失败:', error)
        console.error('错误详情:', error.response || error.message)
      }
      return null
    },
    /**
     * 执行一次加密登录尝试
     * @param {Object} params 登录参数
     * @param {{keyId, publicKey}} pubkeyData RSA公钥信息
     * @returns {Promise<Object>} 接口响应
     */
    async _trySecureLogin(params, pubkeyData) {
      const { keyId, publicKey } = pubkeyData
      const { encryptedAesKey, encryptedBody, aesKeyBytes } = await encryptLoginRequest(publicKey, params)
      const secureParams = { keyId, encryptedAesKey, encryptedBody, appKey: params.appKey || '' }
      const res = await this.$axios.post('/api/v1/manage/login/secure', secureParams)
      if (res && res.code === 1 && res.data && res.data.encryptedResponse) {
        try {
          const decryptedJson = await aesDecryptFn(res.data.encryptedResponse, aesKeyBytes)
          return JSON.parse(decryptedJson)
        } catch (e) {
          return res
        }
      }
      return res
    },
    /**
     * 加密登录核心方法（全程加密，禁止明文降级）
     * 流程：用缓存/新取 pubkey → 加密请求 → POST /secure
     * 若返回 90001（公钥过期），清缓存重新获取 pubkey 重试一次
     * 任何阶段失败均抛出错误，由调用方决定处理方式
     * @param {Object} params 登录参数
     * @returns {Promise<Object>} 接口响应（始终为加密登录结果）
     * @throws {Error} 当加密流程失败时抛出错误
     */
    async _doSecureLogin(params) {
      // 1. 获取公钥（优先缓存）
      let pubkeyData = await this._getPubkey()
      if (!pubkeyData) {
        console.error('获取公钥失败，当前baseURL:', this.$axios.defaults.baseURL)
        console.error('当前hostname:', window.location.hostname)
        throw new Error('ENCRYPT_INIT_FAILED')
      }

      // 2. 首次尝试加密登录
      const res = await this._trySecureLogin(params, pubkeyData)

      // 3. 若公钥已过期，清缓存立即重新取 pubkey 重试一次（禁止降级到明文）
      if (res && (res.code === 90001 || res.code === 90003)) {
        this._pubkeyCache = null
        pubkeyData = await this._getPubkey()
        if (!pubkeyData) {
          throw new Error('ENCRYPT_INIT_FAILED')
        }
        return await this._trySecureLogin(params, pubkeyData)
      }

      return res
    },

    /**
     * 获取多平台
     */
    getLoginCompany(params) {
      if (!params) return;
      this.$axios
        .get("/api/v1/manage/login/company", params)
        .then(({ code, data }) => {
          if (code === 1) {
            this.manyCompanyBox.content = data || [];
            this.manyCompanyBox.show = true;
          }
        })
        .catch(() => { });
    },
    clickManyCompanyFn(company) {
      if (!company.isPassword) {
        this.$message.warning("该平台的登录密码错误");
        return;
      }
      this.manyCompanyBox.show = false;
      this.loginFn(company);
    },
    /**
     * 获取Footer配置
     */
    getFooterConfig() {
      const res = loginMock.getFooterConfig()
      if (res.code === 1) {
        this.footerConfig = res.data
      }
    },
    /**
     * 检测内容是否为 Markdown 格式
     */
    isMarkdownContent(content) {
      if (!content) return false;
      const mdPatterns = [/^#{1,6}\s+/m, /^[-*+]\s+/m, /^\d+\.\s+/m, /^```/m, /^>\s+/m, /\|.*\|/m];
      return mdPatterns.filter(p => p.test(content)).length >= 2;
    },
    /**
     * 查看协议
     * @param {string} type - agreement: 服务协议, privacy: 隐私政策, 或 usePage 数字
     */
    async viewAgreement(type) {
      this.agreementDialog.loading = true
      this.agreementDialog.visible = true
      this.agreementDialog.data = { title: '', version: '', updateTime: null, content: '' }
      
      // 映射 type 到 usePage
      const usePageMap = { agreement: 13, privacy: 1 }
      const usePage = usePageMap[type] || type
      
      try {
        // 优先从后端加载协议内容
        const appKey = this.info && this.info.appKey ? this.info.appKey : window.configure && window.configure.appKey
        if (appKey) {
          const res = await this.$axios.get('/api/v1/manage/protocol/' + 'list', { params: { appKey } })
          if (res && res.code === 1 && res.data && res.data.length > 0) {
            const matched = res.data.find(item => item.usePage === usePage && item.status === 1)
            if (matched) {
              this.agreementDialog.title = matched.title
              this.agreementDialog.data = {
                title: matched.title,
                version: 'V' + (matched.version || 1),
                updateTime: matched.updated || matched.created,
                content: matched.content,
                summary: matched.summary,
                isMarkdown: this.isMarkdownContent(matched.content)
              }
              this.agreementDialog.loading = false
              return
            }
          }
        }
      } catch (e) { /* 降级到 mock 数据 */ }
      
      // 降级：使用 loginMock 数据
      const res = loginMock.getAgreementByType(type)
      if (res.code === 1 && res.data) {
        this.agreementDialog.title = res.data.title
        this.agreementDialog.data = {
          ...res.data,
          isMarkdown: this.isMarkdownContent(res.data.content)
        }
      }
      this.agreementDialog.loading = false
    },
    /**
     * 协议阅读确认（关闭弹窗并上报记录）
     */
    async handleAgreementConfirm() {
      this.agreementDialog.visible = false
      const data = this.agreementDialog.data
      if (data && data.id) {
        const appKey = this.info && this.info.appKey ? this.info.appKey : (window.configure && window.configure.appKey)
        if (appKey) {
          await confirmProtocol(this.$axios, {
            appKey,
            agreementId: data.id,
            userType: USER_TYPE.ADMIN,
            userId: 0,
            userName: '',
          })
        }
      }
    },
    /**
     * 格式化协议更新时间
     */
    formatAgreementTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}/${month}/${day}`
    }
  },
  created() {
    this.getSystemInfo();
    this.getFooterConfig();
    this.$watermark.clear()
    // RSA公钥缓存挂载为实例属性（非响应式，避免 Vue 响应式开销）
    this._pubkeyCache = null
  },
  mounted() {
    // 页面加载动画
    setTimeout(() => {
      this.pageLoading = false
    }, 500)
    
    // 预加载RSA公钥，减少登录时等待和keyId过期概率
    this._preloadPubkey()
    
    // 绑定按钮事件
    let that = this;
    document.addEventListener("keydown", that.keydownFn);
    let login = sessionStorage.getItem("login");
    let isSave = sessionStorage.getItem("isSave");
    if (login && JSON.parse(login) && isSave) {
      this.form = JSON.parse(login);
    }
    if (isSave && JSON.parse(isSave)) {
      this.isSave = JSON.parse(isSave);
    }
  },
  beforeDestroy() {
    // 解绑按钮事件
    let that = this;
    document.removeEventListener("keydown", that.keydownFn);
  },
};
</script>

<!-- 样式已分离到 login.less -->
<style lang="less">
@import './login.less';
</style>
